<script lang="ts" setup>
import AddressField from '@/components/common/AddressField.vue';
import RichTextFieldSmall from '@/components/common/RichTextFieldSmall.vue';
import { Step, Action } from '@/models/action.model';
import { PropType, ref, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: Object as PropType<Step[]>,
    required: true,
  },
  action: {
    type: Object as PropType<Action>,
    required: true,
  },
});

const steps = ref<Step[]>([
  ...(props.modelValue || []).sort((a, b) => a.index - b.index),
]);

const emit = defineEmits(['update:modelValue', 'change']);

function update() {
  emit('update:modelValue', steps);
  emit('change');
}

function addStep() {
  steps.value.push({
    description: '',
    index: steps.value.length,
    address: {
      street: '',
      house_number: '',
      postal_code: '',
      city: '',
      country: 'NL',
    },
  });
  update();
}

function removeStep(index: number) {
  steps.value.splice(index, 1);
  update();
}

function moveUp(answer: Step) {
  const index = steps.value.findIndex((a) => a.id === answer.id);

  steps.value = [
    ...steps.value.slice(0, index - 1),
    answer,
    steps.value[index - 1],
    ...steps.value.slice(index + 1),
  ].map((v, index) => ({ ...v, index }));

  update();
}

function moveDown(answer: Step) {
  const index = steps.value.findIndex((a) => a.id === answer.id);

  steps.value = [
    ...steps.value.slice(0, index),
    steps.value[index + 1],
    answer,
    ...steps.value.slice(index + 2),
  ].map((v, index) => ({ ...v, index }));

  update();
}

watch(
  () => props.modelValue,
  () => {
    Object.assign(steps.value, props.modelValue);
  },
  { deep: true },
);

const showDetails = ref<{ [index: number]: boolean }>({});
</script>
<template>
  <div style="margin: 1em 0">
    <h3>Steps</h3>
    <v-list>
      <v-list-item
        v-for="(step, index) in [...steps].sort((a, b) => a.index - b.index)"
        :key="index"
      >
        <div class="step">
          <v-text-field
            v-model="step.description"
            label="Description"
            :placeholder="`Step ${index + 1}`"
            :append-icon="
              showDetails[index]
                ? 'mdi-menu-up-outline'
                : 'mdi-menu-down-outline'
            "
            @click:append="showDetails[index] = !showDetails[index]"
            required
            hide-details="auto"
            @change="update"
          ></v-text-field>

          &nbsp;
          <RichTextFieldSmall
            :text="steps[index].description"
            @change="(v) => (step.description = v)"
            styles="position: relative; top: 10px; left: 8px"
          ></RichTextFieldSmall>
          &nbsp;
          <v-btn
            icon="mdi-arrow-up"
            class="remove-button"
            density="compact"
            :disabled="index === 0 || step.id === undefined"
            @click.prevent="moveUp(step)"
          ></v-btn>
          &nbsp;
          <v-btn
            icon="mdi-arrow-down"
            class="remove-button"
            density="compact"
            :disabled="index === steps.length - 1 || step.id === undefined"
            @click.prevent="moveDown(step)"
          ></v-btn>
          &nbsp;
          <v-btn
            icon="mdi-delete-outline"
            class="remove-button"
            density="compact"
            @click.prevent="removeStep(index)"
          ></v-btn>
        </div>
        <div>
          <v-chip
            density="compact"
            class="mt-2 ml-4"
            color="warning"
            v-if="step.address.street"
            >Filtered on location</v-chip
          >
        </div>
        <v-card class="step-details" v-if="showDetails[index]" elevation="4">
          <AddressField v-model="step.address" @change="update" />
        </v-card>
      </v-list-item>
    </v-list>
    <v-btn @click="addStep">Add Step</v-btn>
  </div>
</template>
<style scoped>
.step {
  display: flex;
  flex-direction: row;
  padding-right: 16px;
}
.step-details {
  padding: 16px;
  border-radius: 16px;
  margin: 16px 0;
}
.remove-button {
  position: relative;
  top: 10px;
  left: 8px;
}
</style>
